import { useCallback, useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useUnsavedGuard } from 'contexts/UnsavedGuard'
import { useOktaAuth } from '@okta/okta-react'
import { Icon, Avatar, Badge, Dropdown } from '@pepsico-ds/ui'
import { PAGES } from 'common/app-constants'
import { PROGRAM_NAMES } from 'helpers/appConstant'
import Loader from 'ui/components/Loader'
import { LANDING_SERVICE } from 'helpers/constants'
import { GsptWebsiteHeader } from 'pepsico-ds-inner-source'
import { useProgram } from 'ui/hooks/useProgram'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import useApiRequest from 'ui/utilities/useApiRequest'
import HeaderNotifications from './HeaderNotifications'
import SimpleProfileDropdown from './SimpleProfileDropdown'
// Feature key that controls visibility of the Actuals tab
const FEATURE_KEY_SHOW_ACTUALS = 'gspt-release-1A'
const MAX_NOTIFICATION_BADGE_COUNT = 9

function Header() {
  const { selectedProgram, programs, loading: programsLoading, setSelectedProgram } = useProgram()
  const isPfna = selectedProgram?.displayText?.toLowerCase() === PROGRAM_NAMES.pfna.toLowerCase()

  const [programDropdownVersion, setProgramDropdownVersion] = useState(0)
  // const showActuals = useFeatureIsOn(FEATURE_KEY_SHOW_ACTUALS)
  const showActuals = true

  // State
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState([])

  // Hooks
  const navigate = useNavigate()
  const location = useLocation()
  const userDetails = useSelector((state) => state.userDetails)
  const { postData } = useApiRequest()

  // Send notification API call
  const sendNotification = async () => {
    const userId = userDetails?.appUser?.id
    if (!userId) return
    const url = `${LANDING_SERVICE}/notification/send?userId=${userId}`

    try {
      const response = await postData(url, {})
      // eslint-disable-next-line no-console
      // Store the response in state
      if (Array.isArray(response)) {
        setNotifications(response)
      } else if (response && Array.isArray(response?.data)) {
        setNotifications(response?.data)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Notification API Error:', error)
      setNotifications([])
    }
  }

  // Call notification API on component mount
  useEffect(() => {
    if (userDetails?.appUser?.id) {
      sendNotification()
    }
  }, [userDetails?.appUser?.id])
  const { oktaAuth } = useOktaAuth()
  const { confirmOrProceed, suppressGuard } = useUnsavedGuard()
  const [headerVersion, setHeaderVersion] = useState(0)

  // Build navigation items (conditionally hide Actuals when feature flag is off)
  const navItems = useMemo(() => {
    const items = [
      { path: PAGES.DASHBOARD, label: 'Executive Summary' },
      { path: PAGES.MASTER_DATA, label: 'Master data' },
      { path: PAGES.ACTUALS, label: 'Actuals' },
      { path: PAGES.DEMAND_UPDATES, label: 'Demand Strategy' },
      { path: PAGES.IMPORT_EXPORT, label: 'Import and Export' },
      { path: PAGES.GLOSSARY, label: 'Glossary' },
    ]
    return items.filter((item) => {
      if (isPfna && item.path === PAGES.DASHBOARD) return false
      return showActuals || (item.path !== PAGES.ACTUALS && item.path !== PAGES.DASHBOARD)
    })
  }, [showActuals, isPfna])
  // Handlers
  const navigateToTab = (tabIndex) => {
    if (navItems[tabIndex]) {
      confirmOrProceed(() => {
        navigate(navItems[tabIndex].path)
        setActiveTab(tabIndex)
      })
    }
    // keep current tab visually until user confirms
    setHeaderVersion((v) => v + 1)
  }

  const handleProgramChange = useCallback(
    (selected) => {
      const selectedItem = selected?.[0]
      if (!selectedItem) return
      const isValid =
        selectedItem.id &&
        selectedItem.displayText &&
        programs.some((p) => p.id === selectedItem.id)
      if (!isValid) return
      // Always confirm-first; provider decides whether to prompt
      confirmOrProceed(() => {
        setSelectedProgram(selectedItem)
        const targetPath =
          selectedItem.displayText.toLowerCase() === PROGRAM_NAMES.pfna.toLowerCase()
            ? PAGES.MASTER_DATA
            : PAGES.DASHBOARD
        navigate(targetPath, { replace: true })
      })
      // Keep current selection visually until confirmation completes
      setProgramDropdownVersion((v) => v + 1)
    },
    [programs, confirmOrProceed, setSelectedProgram, navigate]
  )

  // useEffect which checks selectedProgram is part of programList or not if not take 0 index by default and setup in setSelectedProgram
  useEffect(() => {
    if (programs.length === 0) return
    if (!selectedProgram || !programs.some((p) => p.id === selectedProgram.id)) {
      setSelectedProgram(programs[0])
    }
  }, [programs, selectedProgram, setSelectedProgram])

  const logout = async () => {
    setLoading(true)
    // Suppress unsaved changes prompts during logout
    suppressGuard()

    try {
      const tokens = await oktaAuth.tokenManager.getTokens()
      const idToken = tokens?.idToken

      // Broadcast logout so other tabs sign out too
      try {
        localStorage.setItem('AGRO_GSAT_BROADCAST_LOGOUT', String(Date.now()))
      } catch (e) {
        // ignore storage errors
      }

      const consent = localStorage.getItem('cookieConsent')
      if (consent === 'declined') {
        try {
          sessionStorage.removeItem('cookiePopoverDismissedThisSession')
        } catch (e) {
          // ignore storage errors
        }
      }

      await oktaAuth.signOut({
        // Ensure RP-initiated logout ends the Okta session
        idToken,
        // Ask SDK to revoke tokens for you (don’t revoke manually beforehand)
        revokeAccessToken: true,
        revokeRefreshToken: true,
        // Send the user to a page that doesn't auto-trigger login
        postLogoutRedirectUri: `${window.location.origin}`,
      })

      // NOTE: In most cases the call above will redirect the browser;
      // code below may not run.
    } catch (e) {
      console.error('Logout error:', e)
      setLoading(false)
    }
  }

  // WebsiteHeader configuration
  const tabOptions = navItems.map((item, index) => ({
    label: item.label,
    onClick: () => navigateToTab(index),
    isActive: activeTab === index,
  }))

  const customToolbarIcons = [
    {
      icon: (
        <>
          <Icon icon="notifications" size="large" style={{ cursor: 'pointer' }} />
          <Badge
            limitMaxDigits={3}
            type="default"
            value={
              notifications?.length > MAX_NOTIFICATION_BADGE_COUNT
                ? `${MAX_NOTIFICATION_BADGE_COUNT}+`
                : notifications?.length
            }
          />
        </>
      ),
      dropdownContent: (
        <HeaderNotifications
          notificationCount={notifications?.length}
          notifications={notifications}
        />
      ),
    },
  ]

  // Effects
  useEffect(() => {
    const currentPath = location.pathname
    if (isPfna && currentPath === PAGES.DASHBOARD) {
      navigate(PAGES.MASTER_DATA, { replace: true })
      return
    }
    const isVisible = navItems.some((i) => i.path === currentPath)
    const hasImportExportAndDetails =
      currentPath.includes('/import-export') && currentPath.includes('details')
    // If current route is hidden due to feature flag, redirect to Demand Strategy;
    // fallback to first available tab if Demand Strategy is not present
    if (!isVisible && !hasImportExportAndDetails) {
      const target = navItems.find((i) => i.path === PAGES.DEMAND_UPDATES) || navItems[0]
      if (target) {
        navigate(target.path, { replace: true })
      }
      return
    }
    // Explicitly redirect Dashboard to Demand Strategy when Home is hidden
    if (!showActuals && currentPath === PAGES.DASHBOARD && !hasImportExportAndDetails) {
      const target = navItems.find((i) => i.path === PAGES.DEMAND_UPDATES)
      if (target) {
        navigate(target.path, { replace: true })
      }
    }
  }, [showActuals, isPfna, location.pathname, navItems, navigate])

  // COMMENTED OUT: Duplicate useEffect - identical to the one above (lines 200-221)
  // This duplication was causing 74% code duplication in SonarQube
  // Keeping for reference in case there was an intended difference
  /*
  useEffect(() => {
    const currentPath = location.pathname
    const isVisible = navItems.some((i) => i.path === currentPath)
    const hasImportExportAndDetails =
      currentPath.includes('/import-export') && currentPath.includes('details')

    // If current route is hidden due to feature flag, redirect to Demand Strategy;
    // fallback to first available tab if Demand Strategy is not present
    if (!isVisible && !hasImportExportAndDetails) {
      const target = navItems.find((i) => i.path === PAGES.DEMAND_UPDATES) || navItems[0]
      if (target) {
        navigate(target.path, { replace: true })
      }
      return
    }

    // Explicitly redirect Dashboard to Demand Strategy when Home is hidden
    if (!showActuals && currentPath === PAGES.DASHBOARD && !hasImportExportAndDetails) {
      const target = navItems.find((i) => i.path === PAGES.DEMAND_UPDATES)
      if (target) {
        navigate(target.path, { replace: true })
      }
    }
  }, [showActuals, location.pathname, navItems, navigate])
  */

  useEffect(() => {
    const currentPath = location.pathname
    const newActiveTab = navItems.findIndex((i) => i.path === currentPath)
    if (newActiveTab !== -1 && newActiveTab !== activeTab) {
      setActiveTab(newActiveTab)
    }
  }, [location, activeTab, navItems])

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <GsptWebsiteHeader
        key={headerVersion}
        logoType="fulllogo"
        status="signedIn"
        tabOptions={tabOptions}
        customToolbarIcons={customToolbarIcons}
        slotComponent={
          programsLoading || !selectedProgram || programs.length === 0 ? (
            <Dropdown width="208px" skeleton childList={[]} />
          ) : (
            <Dropdown
              key={programDropdownVersion}
              childList={programs}
              dropdownHeader="Select Program"
              placeholder="Select Program"
              selectedValue={selectedProgram ? [selectedProgram] : []}
              selection="single"
              setSelectedValue={handleProgramChange}
              size="medium"
              width="100%"
              isClearable={false}
              hideMenuListOnSelection
            />
          )
        }
        avatar={{
          icon: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                imageUrl="https://randomuser.me/api/portraits/men/1.jpg"
                name="Peter"
                size="medium"
                type="image"
              />
              <Icon icon="arrow_drop_down" size="large" />
            </div>
          ),
          dropdownContent: <SimpleProfileDropdown onLogout={logout} />,
        }}
      />
    </>
  )
}

export default Header
